/**
 * UpdateDeliveryProgressModal Component
 * Allows users to update itemsDelivered for each order in a drop
 */

import React, { useState, useEffect } from "react";
import {
  Modal,
  Form,
  InputNumber,
  Button,
  Space,
  Typography,
  Alert,
  List,
  Tag,
  Divider,
  Collapse,
  Spin,
} from "antd";
import { CheckCircleOutlined, WarningOutlined } from "@ant-design/icons";
import { updateOrderDeliveryProgress } from "../services/dropService";
import { getOrder } from "../../orders/services/orderService";
import { ItemSetService } from "../../../shared/services/itemSetService.js";
import StatusChangeConfirmationModal from "./StatusChangeConfirmationModal";
import { logError, logInfo } from "../../../shared/utils/logger.js";

const { Text, Title } = Typography;
const { Panel } = Collapse;

const UpdateDeliveryProgressModal = ({
  open,
  drop,
  onSuccess,
  onCancel,
  loading = false,
}) => {
  const [form] = Form.useForm();
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState(null);
  const [progress, setProgress] = useState({});
  const [initialValues, setInitialValues] = useState({}); // Store initial values for comparison
  const [confirmationModalOpen, setConfirmationModalOpen] = useState(false);
  const [statusChanges, setStatusChanges] = useState({});
  const [pendingUpdates, setPendingUpdates] = useState([]);
  const [itemsLoading, setItemsLoading] = useState(false);
  const [orderItemsById, setOrderItemsById] = useState({});

  useEffect(() => {
    if (open && drop && drop.orders) {
      // Initialize form with current itemsDelivered values
      const initialValuesMap = {};
      const progressMap = {};

      drop.orders.forEach((order) => {
        const orderId = typeof order === "string" ? order : order.orderId;
        const itemsDelivered = typeof order === "object" ? order.itemsDelivered || 0 : 0;
        const itemsTotal = typeof order === "object" ? order.itemsTotal || 0 : 0;

        initialValuesMap[orderId] = itemsDelivered;
        progressMap[orderId] = {
          itemsDelivered,
          itemsTotal,
          percentage: itemsTotal > 0 ? Math.round((itemsDelivered / itemsTotal) * 100) : 0,
        };
      });

      form.setFieldsValue(initialValuesMap);
      setInitialValues(initialValuesMap); // Store initial values
      setProgress(progressMap);
      setError(null);
    }
  }, [open, drop, form]);

  useEffect(() => {
    const fetchOrderItems = async () => {
      if (!open || !drop?.orders || drop.orders.length === 0) return;

      try {
        setItemsLoading(true);

        const entries = await Promise.all(
          drop.orders.map(async (dropOrder) => {
            const orderId =
              typeof dropOrder === "string" ? dropOrder : dropOrder.orderId;
            const embeddedOrder =
              typeof dropOrder === "object" ? dropOrder.order : null;
            const itemSetId = embeddedOrder?.itemSetId;

            if (!orderId || !itemSetId) {
              return [orderId, []];
            }

            const items = await ItemSetService.getItemsBySetId(itemSetId);
            return [orderId, items];
          })
        );

        const itemsMap = {};
        entries.forEach(([orderId, items]) => {
          if (orderId) itemsMap[orderId] = Array.isArray(items) ? items : [];
        });

        setOrderItemsById(itemsMap);

        // Update progress totals using source-of-truth item sets (prevents stale/wrong totals)
        setProgress((prev) => {
          const next = { ...prev };

          drop.orders.forEach((dropOrder) => {
            const orderId =
              typeof dropOrder === "string" ? dropOrder : dropOrder.orderId;
            if (!orderId) return;

            const computedTotal = (itemsMap[orderId] || []).reduce(
              (sum, item) => sum + (Number(item?.quantity) > 0 ? Number(item.quantity) : 1),
              0
            );

            if (computedTotal > 0) {
              const current = next[orderId] || {
                itemsDelivered:
                  typeof dropOrder === "object" ? dropOrder.itemsDelivered || 0 : 0,
                itemsTotal:
                  typeof dropOrder === "object" ? dropOrder.itemsTotal || 0 : 0,
              };

              const itemsDelivered = current.itemsDelivered || 0;
              next[orderId] = {
                ...current,
                itemsTotal: computedTotal,
                percentage:
                  computedTotal > 0
                    ? Math.round((itemsDelivered / computedTotal) * 100)
                    : 0,
              };
            }
          });

          return next;
        });
      } catch (err) {
        logError("Failed to fetch item set items for drop orders", {
          dropId: drop?.id,
          error: err.message,
        });
      } finally {
        setItemsLoading(false);
      }
    };

    fetchOrderItems();
  }, [open, drop]);

  // Calculate what status changes will happen based on new progress
  const calculateStatusChanges = async (updates) => {
    const changes = {};
    
    for (const update of updates) {
      const { orderId, itemsDelivered } = update;
      const currentProgress = progress[orderId];
      if (!currentProgress) continue;

      // Calculate new progress percentage
      const newPercentage = currentProgress.itemsTotal > 0 
        ? Math.round((itemsDelivered / currentProgress.itemsTotal) * 100) 
        : 0;

      // Determine new status based on progress
      let newStatus = null;
      if (newPercentage === 100) {
        newStatus = "completed";
      } else if (newPercentage > 0 && newPercentage < 100) {
        newStatus = "in_progress";
      } else if (newPercentage === 0) {
        // Status might change from pending to in_progress if delivery started
        newStatus = "in_progress";
      }

      // Fetch order to get current status and customer info
      try {
        const order = await getOrder(orderId);
        const currentStatus = order.status || "pending";
        
        // Only include if status will change
        if (newStatus && newStatus !== currentStatus) {
          changes[orderId] = {
            oldStatus: currentStatus,
            newStatus,
            order,
            customerEmail: order.customerEmail || null,
            customerName: order.customer || "Unknown",
            itemsDelivered,
            itemsTotal: currentProgress.itemsTotal,
            progressPercentage: newPercentage,
          };
        }
      } catch (orderError) {
        logError("Failed to fetch order for status calculation", {
          orderId,
          error: orderError.message,
        });
        // Continue without this order's status change info
      }
    }

    return changes;
  };

  const handleSubmit = async () => {
    try {
      // Validate form first
      const values = await form.validateFields();
      
      setError(null);

      const updates = [];

      // Collect all updates that will be made
      for (const [orderId, itemsDelivered] of Object.entries(values)) {
        // Compare against initial values, not current progress state
        const initialValue = initialValues[orderId] ?? 0;
        
        // Only update if value has changed
        if (itemsDelivered !== initialValue) {
          const currentProgress = progress[orderId];
          if (!currentProgress) continue;

          // Validate itemsDelivered doesn't exceed itemsTotal
          if (itemsDelivered > currentProgress.itemsTotal) {
            setError(
              `Order ${orderId}: Items delivered (${itemsDelivered}) cannot exceed items total (${currentProgress.itemsTotal})`
            );
            return;
          }

          if (itemsDelivered < 0) {
            setError(`Order ${orderId}: Items delivered cannot be negative`);
            return;
          }

          updates.push({ orderId, itemsDelivered });
        }
      }

      if (updates.length === 0) {
        // No changes made
        if (onCancel) {
          onCancel();
        }
        return;
      }

      // Calculate status changes and fetch customer info
      setUpdating(true);
      const statusChangesData = await calculateStatusChanges(updates);
      setUpdating(false);

      // If there are status changes, show confirmation modal
      if (Object.keys(statusChangesData).length > 0) {
        setStatusChanges(statusChangesData);
        setPendingUpdates(updates);
        setConfirmationModalOpen(true);
      } else {
        // No status changes, proceed directly with updates
        await executeUpdates(updates, {});
      }
    } catch (err) {
      // Handle validation errors
      if (err.errorFields) {
        setError("Please fix validation errors before submitting");
        setUpdating(false);
        return;
      }
      
      logError("Error updating delivery progress", {
        dropId: drop?.id,
        error: err.message,
        stack: err.stack,
      });
      setError(err.message || "Failed to update delivery progress");
      setUpdating(false);
    }
  };

  const executeUpdates = async (updates, emailPreferences, options = {}) => {
    setUpdating(true);
    setError(null);

    const results = [];

    // Update each order's delivery progress with skipEmail flag
    for (const update of updates) {
      const { orderId, itemsDelivered } = update;
      const skipEmail = emailPreferences[orderId] === false; // false means don't send email

      try {
        await updateOrderDeliveryProgress(drop.id, orderId, itemsDelivered, {
          skipEmail,
          includeReviewRequest: !!options.includeReviewRequest,
        });
        results.push({ orderId, itemsDelivered, success: true });
        logInfo("Delivery progress updated", {
          dropId: drop.id,
          orderId,
          itemsDelivered,
          skipEmail,
          includeReviewRequest: !!options.includeReviewRequest,
        });
      } catch (updateError) {
        results.push({ orderId, itemsDelivered, success: false, error: updateError.message });
        logError("Failed to update delivery progress", {
          dropId: drop.id,
          orderId,
          itemsDelivered,
          error: updateError.message,
        });
      }
    }

    const successCount = results.filter((r) => r.success).length;
    const failCount = results.filter((r) => !r.success).length;

    if (failCount > 0) {
      setError(
        `Updated ${successCount} order(s), but ${failCount} update(s) failed. Please try again.`
      );
      setUpdating(false);
    } else {
      // Success - refresh drop data
      setUpdating(false);
      setConfirmationModalOpen(false);
      setStatusChanges({});
      setPendingUpdates([]);
      if (onSuccess) {
        onSuccess();
      }
    }
  };

  const handleConfirmationConfirm = async (emailPreferences, options = {}) => {
    await executeUpdates(pendingUpdates, emailPreferences, options);
  };

  const handleConfirmationCancel = () => {
    setConfirmationModalOpen(false);
    setStatusChanges({});
    setPendingUpdates([]);
  };

  const handleCancel = () => {
    form.resetFields();
    setError(null);
    setProgress({});
    setInitialValues({}); // Reset initial values
    if (onCancel) {
      onCancel();
    }
  };

  if (!drop || !drop.orders || drop.orders.length === 0) {
    return null;
  }

  const allComplete =
    drop.orders.length > 0 &&
    drop.orders.every((order) => {
      const orderId = typeof order === "string" ? order : order.orderId;
      const p = progress[orderId];
      const itemsDelivered = p?.itemsDelivered || 0;
      const itemsTotal = p?.itemsTotal || 0;
      return itemsTotal > 0 && itemsDelivered === itemsTotal;
    });

  return (
    <Modal
      title={
        <Space>
          <CheckCircleOutlined style={{ color: "#1890ff" }} />
          <Title level={4} style={{ margin: 0 }}>
            Update Delivery Progress
          </Title>
        </Space>
      }
      open={open}
      onCancel={handleCancel}
      footer={null}
      width={700}
      destroyOnHidden
    >
      {error && (
        <Alert
          message="Error"
          description={error}
          type="error"
          showIcon
          style={{ marginBottom: 24 }}
          closable
          onClose={() => setError(null)}
        />
      )}

      {allComplete && (
        <Alert
          message="All items delivered"
          description="All orders in this drop have been fully delivered. You can now complete the drop."
          type="success"
          showIcon
          icon={<CheckCircleOutlined />}
          style={{ marginBottom: 24 }}
        />
      )}

      <Alert
        message="Update delivery progress"
        description="Enter the number of items delivered for each order. Once all items are delivered, you can complete the drop."
        type="info"
        showIcon
        style={{ marginBottom: 24 }}
      />

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <List
          dataSource={drop.orders}
          renderItem={(order) => {
            const orderId = typeof order === "string" ? order : order.orderId;
            const storedItemsTotal = typeof order === "object" ? order.itemsTotal || 0 : 0;
            const computedItemsTotal = (orderItemsById[orderId] || []).reduce(
              (sum, item) => sum + (Number(item?.quantity) > 0 ? Number(item.quantity) : 1),
              0
            );
            const itemsTotal = computedItemsTotal > 0 ? computedItemsTotal : storedItemsTotal;
            const currentProgress = progress[orderId] || { itemsDelivered: 0, itemsTotal: 0 };
            const isComplete = currentProgress.itemsDelivered === itemsTotal && itemsTotal > 0;

            return (
              <List.Item>
                <Space direction="vertical" style={{ width: "100%" }}>
                  <Space>
                    <Text strong>Order: {orderId}</Text>
                    <Tag color={isComplete ? "success" : "default"}>
                      {isComplete ? "Complete" : "In Progress"}
                    </Tag>
                    <Text type="secondary">
                      Total Items: {itemsTotal}
                    </Text>
                  </Space>
                  <Collapse size="small" ghost>
                    <Panel
                      header={
                        <Space>
                          <Text type="secondary">
                            Items ({(orderItemsById[orderId] || []).length || 0})
                          </Text>
                          {itemsLoading && <Spin size="small" />}
                        </Space>
                      }
                      key={`items-${orderId}`}
                    >
                      <List
                        size="small"
                        dataSource={orderItemsById[orderId] || []}
                        locale={{
                          emptyText: itemsLoading ? "Loading items…" : "No items found for this order",
                        }}
                        renderItem={(item, idx) => (
                          <List.Item>
                            <Space style={{ width: "100%", justifyContent: "space-between" }}>
                              <Text>
                                {item?.description || item?.content || `Item ${idx + 1}`}
                              </Text>
                              <Text type="secondary">
                                Qty: {Number(item?.quantity) > 0 ? Number(item.quantity) : 1}
                              </Text>
                            </Space>
                          </List.Item>
                        )}
                      />
                    </Panel>
                  </Collapse>
                  <Form.Item
                    name={orderId}
                    label="Items Delivered"
                    rules={[
                      {
                        required: true,
                        message: "Please enter items delivered",
                      },
                      {
                        type: "number",
                        min: 0,
                        message: "Items delivered cannot be negative",
                      },
                      {
                        validator: (_, value) => {
                          if (value > itemsTotal) {
                            return Promise.reject(
                              new Error(`Cannot exceed total items (${itemsTotal})`)
                            );
                          }
                          return Promise.resolve();
                        },
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <InputNumber
                      min={0}
                      max={itemsTotal}
                      style={{ width: "100%" }}
                      placeholder={`Enter items delivered (max: ${itemsTotal})`}
                      onChange={(value) => {
                        // Update progress state for real-time feedback
                        setProgress((prev) => ({
                          ...prev,
                          [orderId]: {
                            ...prev[orderId],
                            itemsDelivered: value || 0,
                            percentage:
                              itemsTotal > 0 ? Math.round(((value || 0) / itemsTotal) * 100) : 0,
                          },
                        }));
                      }}
                    />
                  </Form.Item>
                  {itemsTotal > 0 && (
                    <div style={{ marginTop: 8 }}>
                      <Text type="secondary" style={{ fontSize: "12px" }}>
                        Progress: {currentProgress.itemsDelivered || 0} / {itemsTotal} (
                        {currentProgress.percentage || 0}%)
                      </Text>
                    </div>
                  )}
                </Space>
              </List.Item>
            );
          }}
        />

        <Divider />

        <Form.Item style={{ marginBottom: 0, marginTop: 24 }}>
          <Space style={{ width: "100%", justifyContent: "flex-end" }}>
            <Button onClick={handleCancel}>Cancel</Button>
            <Button
              type="primary"
              htmlType="submit"
              loading={updating || loading}
              icon={<CheckCircleOutlined />}
            >
              Update Progress
            </Button>
          </Space>
        </Form.Item>
      </Form>

      {/* Status Change Confirmation Modal */}
      <StatusChangeConfirmationModal
        open={confirmationModalOpen}
        statusChanges={statusChanges}
        onConfirm={handleConfirmationConfirm}
        onCancel={handleConfirmationCancel}
        loading={updating}
      />
    </Modal>
  );
};

export default UpdateDeliveryProgressModal;

